import '../../index.css';
import { Suspense, useCallback, useMemo, useState, lazy, useRef } from 'react';
import * as styles from './Home.module.css';

//Assets
import img1 from 'url:../../assets/images/avatar-rate-stack-1.webp';
import img2 from 'url:../../assets/images/avatar-rate-stack-2.webp';
import img3 from 'url:../../assets/images/avatar-rate-stack-3.webp';
import img4 from 'url:../../assets/images/avatar-rate-stack-4.webp';
import logo from 'url:../../assets/images/minipt-icon.webp';
import brandLLGood from 'url:../../assets/images/brand-ll-good.webp';
import brandCnet from 'url:../../assets/images/brand-cnet.webp';
import brandForbes from 'url:../../assets/images/brand-forbes.webp';
import brandMenHealth from 'url:../../assets/images/brand-men-health.webp';
import brandHealthline from 'url:../../assets/images/brand-healthline.webp';
import brandOcbs from 'url:../../assets/images/brand-ocbs.webp';
import brandTechradar from 'url:../../assets/images/brand-techradar.webp';
import brandNewYorkPo from 'url:../../assets/images/brand-new-york-po.webp';

//Components
const StackedImages = lazy(() => import('src/components/StackedImages/StackedImages.tsx'));
const Model3dWithVideo = lazy(
  () => import('src/components/ModelWithVideo/Model3dWithVideo/Model3dWithVideo.tsx'),
);
const StaticCenterLogos = lazy(
  () => import('src/components/StaticCenterLogos/StaticCenterLogos.tsx'),
);
const FeatureCardsList = lazy(() => import('src/components/FeatureCardsList/FeatureCardsList.tsx'));

const TeamMemberCard = lazy(() => import('src/components/TeamMemberCard/TeamMemberCard.tsx'));
const MotivationSection = lazy(
  () => import('src/components/MotivationSection/MotivationSection.tsx'),
);
const UserTestimonials = lazy(() => import('src/components/UserTestimonials/UserTestimonials.tsx'));
const BlogSection = lazy(() => import('src/components/BlogSection/BlogSection.tsx'));
const FeatureCardCarouselMobile = lazy(
  () => import('src/components/FeatureCardCarouselMobile/FeatureCardCarouselMobile.tsx'),
);
const FeatureCardCarousel = lazy(
  () => import('src/components/FeatureCardCarousel/FeatureCardCarousel.tsx'),
);

import { MESH_ACCEPT_RATIO } from 'src/components/ModelWithVideo/Model3dWithVideo/constants.ts';
import { MOBILE_SCROLL_HEIGHT, HIDE_SECTIONS } from 'src/config/constants.ts';
import { TEAM_MEMBERS } from 'src/config/team-member.config.ts';
import { USER_TESTIMONIAL } from 'src/config/user-testimonial.config.ts';
import { BLOG_POSTS } from 'src/config/blog-post.config.ts';
import { Button } from 'src/components/Button/Button.tsx';

//Hooks
import { useMobilePhonePin } from 'src/hooks/useMobilePhonePin.ts';
import useTopBackgroundPin from 'src/hooks/useTopBackgroundPin.ts';
import { CanvasWrapper } from 'src/components/ModelWithVideo/Model3dWithVideo/CanvasWrapper.tsx';
import useWindowDimensions from 'src/hooks/useWindowDimensions.ts';
import { uiStore } from 'src/store/uiStore.ts';
import { useAfterDOMReady } from 'src/hooks/useAfterPageLoad.ts';
import { ALL_ASSETS } from 'src/assets';
import { preloadAssets } from 'src/utils/preloadAssets.ts';
import { handleAppDownloadRedirect } from 'src/utils/appDownloadRedirect.ts';
import SEO from 'src/components/SEO';

export function Home() {
  //Const
  const isMobileDevice = useMemo(() => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent), []);
  const { width, height } = useWindowDimensions();
  const isMobile = width <= 768;

  const showVideoPlayerVisible = uiStore((s) => s.showVideoPlayerVisible);

  //State
  const mobileStoryboardScrollProgress = useRef(0);
  const [mobileFeatureScrollProgress, setMobileFeatureScrollProgress] = useState(0);
  const [showSection, setShowSection] = useState(false);

  //Derived
  const mobileHeight = useMemo(() => {
    const maxHeight = height - 140;
    return width >= height ? maxHeight : Math.min(maxHeight, (width / MESH_ACCEPT_RATIO) * 0.8);
  }, [width, height]);
  const modelScale = useMemo(() => mobileHeight * 6.8, [mobileHeight]);

  //Load other section later
  useAfterDOMReady(() => {
    setShowSection(true);
    preloadAssets(ALL_ASSETS);
  });

  const onFeatureProgress = useCallback((progress: number) => {
    setMobileFeatureScrollProgress(progress);
  }, []);

  const onStoryboardProgress = useCallback((p: number) => {
    mobileStoryboardScrollProgress.current = p;
  }, []);

  // Main feature 3d phone pin
  useMobilePhonePin({
    triggerClass: `.${styles.phonePinWrapper}`,
    pinClass: `.${styles.cssMobileVideoContainer}`,
    scrollHeight: MOBILE_SCROLL_HEIGHT,
    markers: false,
    onProgress: onStoryboardProgress,
  });

  // Fade background behind 3d phone
  useTopBackgroundPin({
    topBgPinSelector: `.${styles.topBgPinWrapper}`,
    backgroundImageSelector: `.${styles.topBackgroundImageWrapper1}`,
    scrollHeight: MOBILE_SCROLL_HEIGHT,
  });

  const onClickTeamMember = useCallback(
    (index: number) => {
      const item = TEAM_MEMBERS[index];
      showVideoPlayerVisible(item.video);
    },
    [showVideoPlayerVisible],
  );

  return (
    <>
      <SEO
        title="miniPT - Your Personal Training Companion"
        description="Get expert fitness guidance, personalized workouts, and achieve your health goals with miniPT."
        canonicalUrl="/"
      />
      <div className={styles.container}>
        <div className={styles.topBgPinWrapper}>
          <div className={styles.topBackgroundImageWrapper1} />
        </div>

        {/*Image review section*/}
        <div className={styles.topBackgroundImageWrapper}>
          <div className={styles.reviewContainer}>
            <StackedImages className={styles.stackedImages} images={[img1, img2, img3, img4]} />
            <div className={styles.rating}>{'Rated 4.8 from 200+ reviews'}</div>
          </div>

          <div className={styles.title}>
            {'Transform your fitness journey\nwith  '}
            <img src={logo} className={styles.miniPt} alt="" />
            {' miniPT'}
          </div>

          <h3 className={styles.description}>
            {
              'Real-time motion tracking, personalized workouts, and expert\nfeedback — all in your pocket.'
            }
          </h3>
        </div>

        <div className={styles.buttonsRow} style={{ position: 'relative', zIndex: 1 }}>
          {/* <Button onClick={() => setWaitlistModalVisible(true)} variant="primary">
          {'Join the waitlist'}
        </Button>
        <Button onClick={() => setQuizModalVisible(true)} variant="secondary">
          {'Take a free quiz'}
        </Button> */}
          <Button onClick={handleAppDownloadRedirect} variant="primary">
            {'Download the App Now'}
          </Button>
        </div>

        <div className={styles.phonePinWrapper}>
          <div style={{ height: mobileHeight }} className={styles.cssMobileVideoContainer}>
            <CanvasWrapper
              canvasProps={{
                camera: {
                  zoom: 1,
                  position: [0, 0, 400],
                },
                orthographic: true,
                resize: { scroll: false },
                dpr: isMobileDevice ? [1, 1.25] : [1, 2],
              }}
            >
              <Suspense fallback={null}>
                <Model3dWithVideo
                  mobileProgress={mobileStoryboardScrollProgress}
                  scale={modelScale}
                  isMobile={isMobile}
                />
              </Suspense>
              {/*<OrbitControls*/}
              {/*  enablePan={true}*/}
              {/*  enableZoom={true}*/}
              {/*  minPolarAngle={Math.PI / 2}*/}
              {/*  maxPolarAngle={Math.PI / 2}*/}
              {/*/>*/}
            </CanvasWrapper>
          </div>
        </div>

        {showSection && !HIDE_SECTIONS.MEDIA_REPORTS && (
          <Suspense fallback={null}>
            <StaticCenterLogos
              className={styles.staticCenterLogos}
              images={[
                brandLLGood,
                brandCnet,
                brandForbes,
                brandMenHealth,
                brandHealthline,
                brandOcbs,
                brandTechradar,
                brandNewYorkPo,
              ]}
              gap={36}
              size={32}
            />
          </Suspense>
        )}

        {showSection && (
          <>
            <div className={styles.bestTimeHeadingText}>
              The best time <br /> to start is{' '}
              <span className={styles.bestTimeHeadingHighlight}>now</span>
            </div>

            <div className={styles.bestTimeSubHeadingText}>
              With us, you’ll stay on track and reach your goals.
            </div>
          </>
        )}

        {showSection && (
          <div className={styles.featureCardWrapper}>
            <Suspense fallback={null}>
              <FeatureCardsList className={styles.featureCard} />
            </Suspense>
          </div>
        )}

        {showSection && (
          <div className={styles.buttonsRowBottom}>
            {/* <Button onClick={() => setWaitlistModalVisible(true)} variant="primary">
            {'Join the waitlist'}
          </Button>
          <Button onClick={() => setQuizModalVisible(true)} variant="secondary">
            {'Take a free quiz'}
          </Button> */}

            <Button onClick={handleAppDownloadRedirect} variant="primary">
              {'Download the App Now'}
            </Button>
          </div>
        )}

        {showSection && (
          <div className={styles.whatsMakeUniqueText}>
            What makes <br /> miniPT <span className={styles.bestTimeHeadingHighlight}>unique</span>
          </div>
        )}

        {showSection && (
          <Suspense fallback={null}>
            {!isMobile && (
              <FeatureCardCarousel
                mobileProgress={mobileFeatureScrollProgress}
                onProgress={onFeatureProgress}
                cssScaleFeature={(height - 250) / 879}
                isMobile={isMobile}
              />
            )}

            {isMobile && (
              <FeatureCardCarouselMobile
                mobileProgress={mobileFeatureScrollProgress}
                onProgress={onFeatureProgress}
                cssScaleFeature={(height - 230) / 879}
                isMobile={isMobile}
              />
            )}
          </Suspense>
        )}

        {showSection && (
          <div className={styles.meetTeamWrapper}>
            <div className={styles.mb60}>
              <div className={styles.whatsMakeUniqueText}>
                Meet the team <br /> behind{' '}
                <span className={styles.bestTimeHeadingHighlight}>miniPT</span>
              </div>
              <div className={styles.teamMemberDescription}>
                {
                  'A group of passionate trainers, fitness experts, and tech innovators dedicated to making your workouts smarter, safer, and more effective.'
                }
              </div>
            </div>

            <div className={styles.teamMemberCardContainer}>
              <Suspense fallback={null}>
                {TEAM_MEMBERS.map((member, index) => (
                  <TeamMemberCard
                    key={member.id}
                    index={index}
                    className={styles.teamMemberCard}
                    image={member.image}
                    video={member.video}
                    name={member.name}
                    role={member.role}
                    linkedinUrl={member.linkedinUrl}
                    onClick={onClickTeamMember}
                  />
                ))}
              </Suspense>
            </div>
          </div>
        )}

        {showSection && (
          <Suspense fallback={null}>
            <MotivationSection className={styles.motivationSection} />
          </Suspense>
        )}

        {showSection && (
          <Suspense fallback={null}>
            <UserTestimonials
              className={styles.userTestimonials}
              title="What our users say"
              linkText="From busy professionals to fitness beginners — discover how miniPT fits into every lifestyle."
              linkUrl="#"
              items={USER_TESTIMONIAL}
            />
          </Suspense>
        )}

        {showSection && !HIDE_SECTIONS.BLOG && (
          <Suspense fallback={null}>
            <BlogSection className={styles.exploreBlog} posts={BLOG_POSTS} />
          </Suspense>
        )}
      </div>
    </>
  );
}
